"use client";

import { useState } from "react";
import Link from "next/link";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type LandingPricingTier = {
  name: string;
  priceMonthly: string;
  priceYearly: string;
  standardReels: string;
  aiVideoReels: string;
  features: string[];
  featured?: boolean;
  ctaLabel: string;
};

const landingPricingTabsListClassName =
  "border border-white/12 bg-white/[0.06] text-white/55 shadow-none";
const landingPricingTabsTriggerClassName =
  "min-w-[7rem] data-[state=active]:text-white";

export function LandingPricingSection({
  tiers,
}: {
  tiers: LandingPricingTier[];
}) {
  const [billingCycle, setBillingCycle] = useState<"month" | "year">("year");

  return (
    <>
      <div className="mt-10 flex w-full justify-center sm:mt-12">
        <Tabs
          onValueChange={(value) => {
            if (value === "month" || value === "year") {
              setBillingCycle(value);
            }
          }}
          value={billingCycle}
        >
          <TabsList
            aria-label="Billing cycle"
            className={landingPricingTabsListClassName}
          >
            <TabsTrigger
              className={landingPricingTabsTriggerClassName}
              value="month"
            >
              Monthly
            </TabsTrigger>
            <TabsTrigger
              className={landingPricingTabsTriggerClassName}
              value="year"
            >
              Yearly
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="mt-10 grid gap-5 lg:grid-cols-3">
        {tiers.map((tier) => (
          <article
            key={tier.name}
            className={[
              "relative rounded-[30px] border p-7 shadow-[0_24px_70px_rgba(0,0,0,0.22)]",
              tier.featured
                ? "border-fuchsia-300/28 bg-[linear-gradient(180deg,rgba(217,70,239,0.16),rgba(255,255,255,0.05))]"
                : "border-white/8 bg-white/[0.03]",
            ].join(" ")}
          >
            <div className="flex items-center justify-between gap-4">
              <h3 className="text-2xl font-semibold text-white">{tier.name}</h3>
              {tier.featured ? (
                <span className="shrink-0 rounded-full border border-fuchsia-300/24 bg-fuchsia-400/12 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-fuchsia-200">
                  Most Popular
                </span>
              ) : null}
            </div>
            <div className="mt-6 flex items-end gap-2">
              <span className="font-display text-5xl tracking-[-0.06em] text-white">
                {billingCycle === "year" ? tier.priceYearly : tier.priceMonthly}
              </span>
              <span className="pb-1 text-sm text-white/48">
                {billingCycle === "year"
                  ? "/ month, billed annually"
                  : "/ month"}
              </span>
            </div>
            <div className="mt-5 grid gap-1.5">
              <p className="m-0 text-sm font-bold leading-snug text-rose-400">
                {tier.standardReels} / mo
              </p>
              <p className="m-0 text-sm font-bold leading-snug text-rose-400">
                or {tier.aiVideoReels} / mo
              </p>
            </div>
            <ul className="mt-6 space-y-3">
              {tier.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-3 text-sm text-white/66"
                >
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-white/8 text-fuchsia-200">
                    <Check className="size-3.5" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
            <Button
              asChild
              variant={tier.featured ? "aura" : "outline"}
              size="lg"
              className="mt-8 h-12 w-full rounded-full border-white/12 bg-white/4 text-white hover:bg-white/8"
            >
              <Link href="/login" prefetch={false}>{tier.ctaLabel}</Link>
            </Button>
          </article>
        ))}
      </div>

      <p className="mx-auto mt-8 max-w-3xl px-2 text-center text-xs leading-relaxed text-white/45 md:text-sm">
        *Estimated monthly output varies by reel length, model choice, and
        generation settings. Top up with credit packs whenever you need more
        room to keep working on your reels.
      </p>
    </>
  );
}
